import { createLogger, initLoggerContext, Logger, setSharedLogger } from '@fiji/common/src/services/logger';

/**
 * Production hostnames that must NOT emit browser-console log noise. Any
 * origin outside this set (dev, QA, staging, local, preview builds) has
 * logging enabled so the team can observe live events during development.
 *
 * Hostnames are checked at runtime, so a single build serves all
 * environments — matches the `window.APP_CONFIG` + CI/CD deploy pattern
 * this repo already uses for `API_URL`, `DYNATRACE_SCRIPT`, etc.
 */
const PROD_HOSTNAMES: ReadonlySet<string> = new Set([
    'brightlayer-remote-monitoring.eaton.com',
    'brightlayer-remote-monitoring-eu.eaton.com',
]);

const isProd = typeof window !== 'undefined' && PROD_HOSTNAMES.has(window.location.hostname);

/**
 * Best-effort user-agent summary parsed once at module load. Returns a
 * short `"Browser Major"` / `"OS Major"` pair (e.g. `"Chrome 120"`,
 * `"macOS 14"`) — deliberately coarse so it fits the Confluence spec's
 * "Technical diagnostics" bucket without leaking a full UA fingerprint.
 *
 * Kept inline (no `ua-parser-js` dep) to avoid pulling ~30KB gzipped for
 * two strings. Falls back to `undefined` on non-browser runtimes (SSR,
 * jsdom without navigator) so the `Logger` simply omits the fields.
 */
const resolveClientInfo = (): { browser?: string; os?: string } => {
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
        return {};
    }
    const ua = navigator.userAgent;
    // Order matters: Edg/OPR embed `Chrome` in their UA, so match them first.
    const browserRegexes: Array<[string, RegExp]> = [
        ['Edge', /Edg\/(\d+)/],
        ['Opera', /OPR\/(\d+)/],
        ['Chrome', /Chrome\/(\d+)/],
        ['Firefox', /Firefox\/(\d+)/],
        ['Safari', /Version\/(\d+).*Safari/],
    ];
    let browser: string | undefined;
    for (const [name, re] of browserRegexes) {
        const m = ua.match(re);
        if (m) {
            browser = `${name} ${m[1]}`;
            break;
        }
    }
    const osRegexes: Array<[string, RegExp]> = [
        ['Windows', /Windows NT (\d+(?:\.\d+)?)/],
        // iOS MUST be tried before macOS: iOS UAs contain the substring
        // `like Mac OS X`, and even though today's macOS regex requires
        // digits immediately after `Mac OS X`, reordering here removes the
        // fragility if a future iOS UA variant ever puts digits there.
        ['iOS', /OS (\d+[_.]\d+) like Mac OS X/],
        ['macOS', /Mac OS X (\d+[_.]\d+)/],
        ['Android', /Android (\d+(?:\.\d+)?)/],
        ['Linux', /Linux/],
    ];
    let os: string | undefined;
    for (const [name, re] of osRegexes) {
        const m = ua.match(re);
        if (m) {
            os = m[1] ? `${name} ${m[1].replace('_', '.')}` : name;
            break;
        }
    }
    return { browser, os };
};

const { browser, os } = resolveClientInfo();

/**
 * Initialize the ambient logger context at module load, before any
 * component mounts or effect fires. React runs passive effects
 * child-to-parent, so if we initialized inside a parent `useEffect`,
 * child effects (e.g. `RouteLogger`) would emit their first event
 * against an empty context and lose `sessionId` correlation. Doing it
 * at import time guarantees every subsequent `logger.*` call — from
 * any component, hook, middleware, or util — sees a valid `sessionId`.
 */
initLoggerContext();

/**
 * Shared logger singleton for the fiji-web app.
 *
 * Import from any component/hook/util and call {@link Logger.info}/`warn`/
 * `error`/`debug`. Every emitted event is auto-populated with the current
 * `sessionId`, `userId`, and `correlationId` from `LoggerContext`.
 *
 * `appVersion` and `environment` come from build-time constants injected by
 * `webpack.DefinePlugin` (see `packages/web/webpack.config.js`). The
 * fallbacks below (`'0.0.0'` / `'dev'`) only trigger when running under a
 * non-webpack entry point (e.g. Jest via ts-jest), never in the shipped
 * bundle.
 *
 * The web bootstrap does not attach the default console transport, so no
 * application log events are printed to the browser console from this logger.
 * Remote delivery (for example Dynatrace) is expected to be wired through an
 * explicit transport instead of relying on console output.
 */
export const logger: Logger = createLogger(
    {
        appName: 'fiji-web',
        appVersion: process.env.REACT_APP_VERSION ?? '0.0.0',
        environment: isProd ? 'prod' : process.env.REACT_APP_ENV ?? 'dev',
        minLevel: isProd ? 'WARN' : 'INFO',
        enabled: !isProd,
        browser,
        os,
    },
    []
);

// Register the singleton so cross-platform code in `packages/common` (e.g.
// the RTK Query base interceptor) can emit `api_call` / `api_error` events
// without importing from `packages/web`. Mobile does the same in its own
// `services/logger.ts` bootstrap.
setSharedLogger(logger);
